import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";

type TooltipVariant = "light" | "dark";

interface MaterialTooltipExtraProps {
  isShow?: boolean;
  centerText?: boolean;
  variant?: TooltipVariant;
  tooltipSx?: Record<string, unknown>;
}

const lightStyles = {
  backgroundColor: "#ffffff",
  color: "#173c4c",
  fontSize: 16,
  boxShadow: "0px 0px 4px rgba(75, 74, 74, 0.25)",
  fontWeight: 400,
  padding: "6px 10px"
};

const darkStyles = {
  backgroundColor: "#071f35",
  color: "#ffffff",
  fontSize: 12,
  lineHeight: "16px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
  fontWeight: 400,
  padding: "8px 12px",
  borderRadius: "6px",
  maxWidth: 260,
  whiteSpace: "pre-line" as const
};

export const MaterialTooltip = styled(
  ({
    className,
    isShow,
    centerText = false,
    variant = "light",
    tooltipSx,
    ...props
  }: TooltipProps & MaterialTooltipExtraProps) => {
    const [isOverflowed, setIsOverflow] = useState<boolean>(false);
    const [tooltipKey, setTooltipKey] = useState<number>(0);
    const textElementRef = useRef<HTMLSpanElement>(null);

    const checkOverflow = useCallback(() => {
      if (textElementRef.current) {
        setIsOverflow(textElementRef.current.scrollWidth > textElementRef.current.clientWidth);
      }
    }, []);

    const handleScroll = useCallback(() => {
      // Force tooltip to reposition by updating key
      setTooltipKey((prev) => prev + 1);
    }, []);

    // Find the closest scrollable container
    const findScrollableParent = useCallback((element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;

      const isElementScrollable = (el: HTMLElement): boolean => {
        const style = window.getComputedStyle(el);
        const scrollableValues = ["auto", "scroll"];
        return scrollableValues.includes(style.overflow) || scrollableValues.includes(style.overflowY);
      };

      if (isElementScrollable(element)) return element;
      return findScrollableParent(element.parentElement);
    }, []);

    useEffect(() => {
      checkOverflow();

      // We often use this component in tables that have scroll bars.
      //  If you call the tooltip and then start scrolling, it will not display correctly,
      //   so we have to close the tooltip while scrolling.
      const scrollableParent = findScrollableParent(textElementRef.current);
      if (scrollableParent) {
        scrollableParent.addEventListener("scroll", handleScroll, { passive: true });
      }

      // Re-check overflow when the element resizes (e.g. table column width settles after initial render).
      const resizeObserver = textElementRef.current ? new ResizeObserver(checkOverflow) : null;
      if (resizeObserver && textElementRef.current) {
        resizeObserver.observe(textElementRef.current);
      }

      return () => {
        if (scrollableParent) {
          scrollableParent.removeEventListener("scroll", handleScroll);
        }
        resizeObserver?.disconnect();
      };
    }, []);

    const isDark = variant === "dark";
    const tooltipStyles = isDark ? darkStyles : lightStyles;
    const arrowColor = isDark ? "#071f35" : "#ffffff";

    return (
      <Tooltip
        arrow
        {...props}
        placement={props.placement || "top"}
        key={tooltipKey}
        classes={{ popper: className }}
        slotProps={{
          tooltip: { sx: { ...tooltipStyles, ...tooltipSx } },
          arrow: { sx: { color: arrowColor } },
          popper: {
            sx: {
              ...(centerText && { textAlign: "center" })
            },
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  boundary: "clippingParents",
                  altBoundary: true,
                  tether: false,
                  rootBoundary: "viewport"
                }
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["top", "bottom", "left", "right"]
                }
              }
            ]
          }
        }}
        ref={textElementRef}
        {...(!isShow ? { disableHoverListener: !isOverflowed } : null)}
      >
        {props.children}
      </Tooltip>
    );
  }
)();
